import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { oneLineBreakLimited } from '../../common/functions';
import { SummaryContainer, SummaryContainerSection, SummaryHeader, useCommonSubmissionSummary } from '../components/commons_submission_summary';
import { ToothViewer } from '../case_submission/tx_plan';
import * as nomenclature from '../../common/nomenclature';
import ThumbnailURLInterface from '../../components/thumbnail/thumbnail_url_interface';

import { getRecordStates } from '../../redux/reducers/record_viewer/record_viewer';
import { buildRecordStates, onLoadImageRecord } from '../../redux/actions/record_viewer/record_viewer';
import { convertDate } from '../../common/functions';

/**
 * Component for displaying summary on de submission page
 * @function
 * @param {object} props - Props object
 * @return {JSX} - JSX for submission summary
 */
const DE4SubmissionSummary = (props) => {
  const { getTeethValue, getAnteriorPosteriorValue, getBracketPlacementValue } = useCommonSubmissionSummary({
    buildRecordStates: props.buildRecordStates,
    photoUpload: props.photoUpload,
    xrayUpload: props.xrayUpload,
    scanUpload: props.scanUpload,
    anteriorPosteriorR: props.de4_anterior_posterior_r,
    anteriorPosteriorL: props.de4_anterior_posterior_l,
    anteriorPosteriorCorrections: props.de4_anterior_posterior_corrections
      ? {
          classIIOrIIICorrectionSimulation: props.de4_anterior_posterior_corrections.class_II_or_III_correction_simulation,
          posteriorIPR: props.de4_anterior_posterior_corrections.posterior_IPR,
        }
      : undefined,
    anteriorPosteriorNotes: props.de4_anterior_posterior_notes,
    bracketTeeth: props.de4_bracket_teeth,
    archToTreat: props.de4_arch_to_treat,
    missingTeeth: props.de4_missing_teeth,
    extractTeeth: props.de4_extract_teeth,
    selectedIteroScanRecords: props.selectedIteroScanRecords,
    uploadMethod: props.uploadMethod,
  });

  const getArchToTreatValue = () => {
    const hasUpperArch = ['upper', 'both'].includes(props.de4_arch_to_treat);
    const hasLowerArch = ['lower', 'both'].includes(props.de4_arch_to_treat);
    return (
      <div>
        {hasUpperArch && <p>Upper arch - {props.de4_is_upper_smartwire_new ? 'Initial' : 'Additional'} Smartwires</p>}
        {hasLowerArch && <p>Lower arch - {props.de4_is_lower_smartwire_new ? 'Initial' : 'Additional'} Smartwires</p>}
      </div>
    );
  };

  const getMostRecentlyInsertedSmartwireValue = () => {
    const upperSmartwireName = getSmartwireName('upper');
    const lowerSmartwireName = getSmartwireName('lower');
    const de4_upper_smartwire_date = props.de4_upper_smartwire_date;
    const de4_lower_smartwire_date = props.de4_lower_smartwire_date;

    const upper_smartwire = de4_upper_smartwire_date ? `${upperSmartwireName} (inserted ${convertDate(de4_upper_smartwire_date)})` : upperSmartwireName;
    const lower_smartwire = de4_lower_smartwire_date ? `${lowerSmartwireName} (inserted ${convertDate(de4_lower_smartwire_date)})` : lowerSmartwireName;

    return (
      <div>
        <p>Upper: {upper_smartwire}</p>
        <p>Lower: {lower_smartwire}</p>
      </div>
    );
  };

  const getSmartwireName = (arch) => {
    const { prevWireSequences = [] } = props;
    const archSeqs = prevWireSequences.filter((ws) => ws.wire_name.includes(arch));

    if (archSeqs?.length) {
      const selection = arch === 'upper' ? props.de4_upper_smartwire : props.de4_lower_smartwire;
      if (selection === 'none') return 'None';
      const wireVerbiage = {
        stage0: 'specialty',
        stage1: 'initial',
        stage2: 'intermediate',
        stage3: 'final',
      };
      const seq = archSeqs.find((ws) => ws.wire_name.includes(wireVerbiage[selection]));
      return `Smartwire ${seq.sequence}`;
    }

    const wireVerbiage = {
      none: 'None',
      stage0: nomenclature.SMARTWIRE_0,
      stage1: nomenclature.SMARTWIRE_1,
      stage2: nomenclature.SMARTWIRE_2,
      stage3: nomenclature.SMARTWIRE_3,
      de0: nomenclature.DE_SMARTWIRE_0,
      de1: nomenclature.DE_SMARTWIRE_1,
      de2: nomenclature.DE_SMARTWIRE_2,
      de3: nomenclature.DE_SMARTWIRE_3,
    };
    return wireVerbiage[arch === 'upper' ? props.de4_upper_smartwire : props.de4_lower_smartwire];
  };

  const getOcclusionValue = () => {
    const occlusionVerbiage = {
      Yes: 'Yes',
      No: 'No - Only move teeth as described below',
    };
    return (
      <div>
        <p>{occlusionVerbiage[props.de4_occlusion]}</p>
        {props.de4_occlusion_notes && props.de4_occlusion === 'No' && <p>"{props.de4_occlusion_notes}"</p>}
      </div>
    );
  };

  const getAdditionalInstructionsValue = () => {
    const text = oneLineBreakLimited(props.de4_additional_instructions).trim();
    if (text) return `"${text}"`;
    return 'N/A';
  };

  const fields = [
    {
      title: 'Arch to treat',
      value: getArchToTreatValue(),
    },
    {
      title: 'Current engaged Smartwire',
      value: getMostRecentlyInsertedSmartwireValue(),
    },
    {
      title: 'Missing teeth (Not including implant, bridge or primary tooth with agenesis)',
      value: getTeethValue(props.de4_missing_teeth),
    },
    {
      title: 'Additional teeth to be extracted',
      value: getTeethValue(props.de4_extract_teeth),
    },

    {
      title: 'Brackets to be included in the new Smartwire design',
      value: getBracketPlacementValue(),
    },
    {
      title: 'Do not move the following teeth',
      value: getTeethValue(props.de4_teeth_restriction),
    },
    {
      title: 'Anterior-Posterior',
      value: getAnteriorPosteriorValue(),
    },
    {
      title: 'Allow InSmile Designers to idealize occlusion',
      value: getOcclusionValue(),
    },
    {
      title: 'Any additional instructions (e.g. Midlines, IPR, etc.)',
      value: getAdditionalInstructionsValue(),
    },
  ];

  return (
    <>
      <SummaryHeader />
      <ThumbnailURLInterface {...props.thumbnailComponentProps} recordScroll={true} />
      <div className="submission-summary">
        <SummaryContainer>
          <SummaryContainerSection>
            <ol className="submission-summary__ol">
              {fields.map((field, index) => {
                return (
                  <li className="submission-summary__field" key={index}>
                    <div className="emphasis">{field.title}</div>
                    <div className="submission-summary__field__value">{field.value}</div>
                  </li>
                );
              })}
            </ol>
          </SummaryContainerSection>
          <SummaryContainerSection>
            <ToothViewer teethSets={props.getImpliedTeethSets()} />
          </SummaryContainerSection>
        </SummaryContainer>
        <div className="line-division--primary" />
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    record_states: getRecordStates(state),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      buildRecordStates: buildRecordStates,
      onLoadImageRecord: onLoadImageRecord,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(DE4SubmissionSummary);
